import { getFreshToken } from '@/lib/firebase';

/**
 * Thin authenticated REST client for the GitLab onboarding endpoints
 * (docs/specs/03-gitlab-automation §H). Unlike the rest of the app (Apollo
 * GraphQL), these are REST + StaffFirebaseGuard, so we attach the Firebase
 * bearer token directly. Base = the GraphQL URL with /graphql stripped.
 */
const API_BASE = (import.meta.env.VITE_GRAPHQL_URL as string).replace(/\/graphql\/?$/, '');

export interface GitlabComplianceReason {
  gate: number;
  message: string;
}
export type GitlabConnectResult =
  | { status: 'connected'; gitlabUsername: string }
  | { status: 'blocked'; reasons: GitlabComplianceReason[] };

export interface GitlabMeStatus {
  connected: boolean;
  gitlabUsername?: string;
  accessLevel?: number;
  offboardedAt?: string;
}

async function authedFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { token } = await getFreshToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `Request failed (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) msg = body.message;
    } catch {
      /* keep default */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

export const gitlabApi = {
  me: () => authedFetch<GitlabMeStatus>('/api/gitlab/me'),
  start: () => authedFetch<{ authorizeUrl: string }>('/api/gitlab/oauth/start'),
  callback: (code: string, state: string) =>
    authedFetch<GitlabConnectResult>('/api/gitlab/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    }),
};

export function accessLevelLabel(level?: number): string | undefined {
  const map: Record<number, string> = { 10: 'Guest', 20: 'Reporter', 30: 'Developer', 40: 'Maintainer', 50: 'Owner' };
  return level == null ? undefined : map[level] ?? `Level ${level}`;
}
